import { httpClient } from '../lib/httpClient';

export interface FitConflict {
  tag: string;
  impact: 'exclusion' | 'tilt' | string;
  direction?: number;
}

/**
 * TASK-827: where one value tag came from.
 *
 * There is deliberately no `'issuer'` source — nothing in this system produces one. Every tag
 * is a GICS industry-group default (optionally region-qualified), which `instrument_tags`
 * declares of itself as demo-grade. Naming the basis is what stops "Tyson Foods breaches your
 * no-luxury-conglomerates red line" reading as a finding about Tyson Foods.
 */
export interface TagProvenance {
  /**
   * TASK-837: `advisor_override` joins the two machine sources. An override is NOT the
   * absence of a tag — it is a DIFFERENT provenance, and rendering it identically to a
   * machine tag would make the fit score unexplainable six months later.
   */
  source: 'industry_group' | 'region' | 'advisor_override';
  /** The group, or "group · region", the tag was applied from. For an override: the direction. */
  basis: string;
  /** TASK-837, override only — who corrected it, when, why, and which row to revert. */
  override_id?: string;
  actor?: string;
  at?: string | null;
  rationale?: string | null;
}

/** TASK-837: one advisor correction to one instrument's value tags, for one client. */
export interface TagOverride {
  id: string;
  isin: string;
  tag: string;
  /** `remove` clears a tag (permissive); `add` applies one (restrictive). */
  direction: 'add' | 'remove';
  rationale: string | null;
  actor: string;
  at: string | null;
  reverted_at?: string | null;
  reverted_by?: string | null;
}

export interface FitHolding {
  position_id: string;
  fit_score: number | null;
  conflicts: FitConflict[] | null;
  isin: string | null;
  security_name: string | null;
  market_value: number | null;
  /** TASK-827: the classification the tags were derived from. */
  industry_group?: string | null;
  region?: string | null;
  /** TASK-830: the bucket a compliant replacement must come from. */
  sub_asset_class?: string | null;
  /** TASK-827: per-tag, where it came from. Absent on an older backend. */
  tag_provenance?: Record<string, TagProvenance>;
  /** TASK-837: the active corrections on this holding. */
  tag_overrides?: TagOverride[];
}

export interface PortfolioFitResponse {
  client_id: string;
  portfolio_fit: number | null;
  coverage: number | null;
  scored_holdings: number;
  total_holdings: number;
  positions_found?: boolean;
  empty_reason?: 'no_positions_found' | string | null;
  holdings: FitHolding[];
}

export interface SwapProposal {
  id: string;
  holding_id: string;
  position_id: string;
  candidate_isin: string | null;
  candidate_valor: string | null;
  dna_reason: string;
  cio_view: string | null;
  fit_gain: number | null;
  mandate_neutral: boolean;
  sources: Array<Record<string, unknown>> | null;
  status: 'pending' | 'approved' | 'skipped';
  created_at: string;
}

export interface SwapResponse {
  client_id: string;
  total: number;
  proposals: SwapProposal[];
}

export interface SwapDecisionResponse {
  client_id: string;
  proposal_id: string;
  status: 'approved' | 'skipped';
  skipped_siblings: number;
}

export const portfolioFitService = {
  getFit(clientId: string) {
    return httpClient.get<PortfolioFitResponse>(`/api/portfolio/${clientId}/fit`);
  },

  getSwaps(clientId: string) {
    return httpClient.get<SwapResponse>(`/api/portfolio/${clientId}/swaps`);
  },

  approveSwap(clientId: string, proposalId: string) {
    return httpClient.post<SwapDecisionResponse>(
      `/api/portfolio/${clientId}/swaps/${proposalId}/approve`,
      {}
    );
  },

  skipSwap(clientId: string, proposalId: string) {
    return httpClient.post<SwapDecisionResponse>(
      `/api/portfolio/${clientId}/swaps/${proposalId}/skip`,
      {}
    );
  },

  /**
   * TASK-837: correct a value tag on one client's holding.
   *
   * `rationale` is REQUIRED for `remove` — it suppresses a suitability signal — and the server
   * refuses without one. The refusal is deliberately not pre-empted here beyond the obvious:
   * the rule belongs to the server (and to a CHECK constraint behind it), and a second copy
   * in the client is the drift TASK-820 spent a ticket removing elsewhere.
   */
  overrideTag(
    clientId: string,
    body: { isin: string; tag: string; direction: 'add' | 'remove'; rationale?: string },
  ) {
    return httpClient.post<{ client_id: string; override_id: string; recomputed?: boolean }>(
      `/api/portfolio/${clientId}/tag-overrides`,
      body,
    );
  },

  revertTagOverride(clientId: string, overrideId: string) {
    return httpClient.post<{ client_id: string; override_id: string; reverted: boolean }>(
      `/api/portfolio/${clientId}/tag-overrides/${overrideId}/revert`,
      {},
    );
  },

  getTagOverrides(clientId: string) {
    return httpClient.get<{ client_id: string; overrides: TagOverride[] }>(
      `/api/portfolio/${clientId}/tag-overrides`,
    );
  },
};
